import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { useTalkFlowDocument } from '@/hooks/apiHooks';
import Markdown from '@/components/Record/TalkFLow/Markdown';
import useCommonSummaryRegenerate from './useCommonSummaryRegenerate';
import useRecordTranslationView from '@/components/Record/TalkFLow/hooks/useRecordTranslationView';
import Skeleton from '@/components/@IntranetLibrary/Skeleton';

// 요약 블록 스켈레톤 바 너비 (본문 대비 컴팩트하게 3줄)
const SKELETON_WIDTHS = ['100%', '92%', '58%'];

type Props = {
  docId: string;
  isOwner: boolean;
};

/**
 * AI 요약 (commonSummary) 표시 블록.
 * - 소스: 문서 GET(`useTalkFlowDocument`) 응답의 `commonSummary.text` (SWR 캐시 공유).
 * - 갱신 타이밍: `/summarize` 완료 시 상위에서 문서 재조회(mutate)하면 자동 반영.
 * - 진행/실패 상태(스켈레톤/오류 문구)는 최초 생성·재생성 공통으로 SSE 를 그대로 따른다:
 *   최초 생성 `talk-flow.common-summary.{start,complete,error}` /
 *   재생성 `talk-flow.common-summary.regenerate.{start,complete,error}`
 *   → start=스켈레톤 / complete=문서 재조회 후 요약 표시 / error=오류 문구 (useCommonSummaryRegenerate).
 * - 재생성: 작성자만 리플레시 버튼 노출 → common-summary/regenerate 호출.
 * - 요약이 없으면(=null/빈 문자열) 블록 자체를 렌더링하지 않음.
 */
const AiSummary = ({ docId, isOwner }: Props) => {
  const { t } = useTranslation();
  const { data } = useTalkFlowDocument(docId);
  const { regenerate, isRegenerating, regenerateFailed } =
    useCommonSummaryRegenerate(docId);

  // 진행/실패는 commonSummary(AI 요약) 재생성 상태만 본다. (organize 파이프라인과 별개)
  // AI 정리(organize) 실패는 본문 전용 에러 배너로 분리했으므로 여기엔 영향을 주지 않는다.
  const isUpdating = isRegenerating;
  const hasError = regenerateFailed;

  const { pick } = useRecordTranslationView(docId);
  // 번역이 켜져 있으면 번역본을, 아니면 원문을 그대로 렌더한다.
  // (번역 결과는 atom 에만 있고 문서 응답을 덮어쓰지 않으므로 재생성/SSE 흐름과 충돌하지 않는다)
  const originalSummaryText = data?.commonSummary?.text?.trim();
  const summaryText = originalSummaryText
    ? pick('summary', originalSummaryText)
    : originalSummaryText;
  // 요약이 아직 없어도 재생성 중(isUpdating)이거나 에러(hasError)면 영역을 노출한다.
  // 요약도 없고 갱신/에러도 아닐 때(최초 생성 전)에만 블록 자체를 숨긴다.
  if (!summaryText && !isUpdating && !hasError) return null;

  return (
    <Wrap>
      <SummaryBox>
        <BadgeRow>
          <Badge>
            <BadgeLabel>{t('AI 요약')}</BadgeLabel>
            {isOwner && (
              <RefreshButton
                type="button"
                onClick={regenerate}
                disabled={isUpdating}
                aria-label={t('AI 요약 다시 생성')}
                // 로딩 스피너 회전 중임을 스크린리더에 알린다
                aria-busy={isUpdating}
                // 진행 중에는 스피너 아이콘을 회전시키고, 정지 상태에서는 새로고침 아이콘 표시
                isSpinning={isUpdating}
              >
                {isUpdating ? (
                  // 로딩 스피너 (회색 트랙 + 핑크 arc)
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="10"
                      cy="10"
                      r="7"
                      stroke="#D4D7DB"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M3 10C3 13.866 6.13401 17 10 17C13.866 17 17 13.866 17 10C17 6.13401 13.866 3 10 3"
                      stroke="#C77AC4"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  // 새로고침 (재생성) 아이콘
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.9987 3.33594C10.576 3.33594 12.6654 5.42528 12.6654 8.0026C12.6654 10.5799 10.576 12.6693 7.9987 12.6693C5.42137 12.6693 3.33203 10.5799 3.33203 8.0026C3.33203 6.53661 4.00801 5.22851 5.06527 4.37297"
                      stroke="#C77AC4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M3.33203 4.11328H5.40611V6.18736"
                      stroke="#C77AC4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </RefreshButton>
            )}
          </Badge>
          {hasError && (
            <ErrorText>
              {t('오류가 발생했습니다. 다시 시도해 주세요.')}
            </ErrorText>
          )}
        </BadgeRow>
        {isUpdating ? (
          // 갱신 중에는 기존 내용을 회색 처리하지 않고 스켈레톤으로 대체
          <SkeletonWrap>
            {SKELETON_WIDTHS.map((width, i) => (
              <Skeleton
                // 고정 순서 목록이라 index key 사용
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                variant="rect"
                width={width}
                height={20}
              />
            ))}
          </SkeletonWrap>
        ) : (
          // 에러 상태에서는 본문 없이 뱃지 우측 에러 문구만 노출한다.
          !hasError &&
          summaryText && (
            <Text>
              <Markdown>{summaryText}</Markdown>
            </Text>
          )
        )}
      </SummaryBox>
    </Wrap>
  );
};

export default AiSummary;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// 요약 박스 아래 본문 탭과 구분하는 divider (상단 여백은 Contents Box 의 padding-top(24px)이 담당)
const Wrap = styled.div`
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid var(--dk-line, #e7e9ec);
`;

// 시안: gray_1 배경 + AI radial 그라디언트 테두리의 요약 박스
const SummaryBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  /* AI Tool/Portal Ai Gradient — 중앙 보라(#afb2f4) → 바깥 핑크(#e1b3df) radial 그라디언트 보더 */
  border: 1px solid transparent;
  border-radius: 8px;
  background-image: linear-gradient(var(--dk-bg-subtle, #f9fafb), var(--dk-bg-subtle, #f9fafb)),
    radial-gradient(#afb2f4 0%, #e1b3df 100%);
  background-origin: border-box;
  background-clip: padding-box, border-box;
`;

// 뱃지 + 에러 문구를 한 줄에 배치 (뱃지 우측 12px, 세로 중앙)
const BadgeRow = styled.div`
  display: flex;
  align-self: flex-start;
  align-items: center;
  gap: 12px;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px 4px 8px;
  background: var(--dk-purple-soft, #f8eff8);
  border-radius: 999px;
`;

const BadgeLabel = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #c77ac4;
`;

// AI 요약 생성 실패 시 뱃지 우측 에러 문구 (Caution #F55060)
const ErrorText = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: var(--dk-danger, #f55060);
`;

const RefreshButton = styled.button<{ isSpinning: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }

  svg {
    display: block;
    animation: ${({ isSpinning }) => (isSpinning ? spin : 'none')} 0.8s linear
      infinite;
  }
`;

const Text = styled.div`
  /* 시안: AI Markdown h5 (Pretendard 14px / line-height 28px) */
  align-self: stretch;
  font-size: 14px;
  font-weight: 400;
  line-height: 28px;
  color: var(--dk-text, #000000);
`;

const SkeletonWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
